/**
 * The standalone feedback page — the link you hand a teacher after a demo.
 *
 * Deliberately reachable without a lesson and without an account: a teacher can
 * arrive from a QR code, pick what they want to talk about, and name a chapter
 * we have not built yet. That last case is the whole point — a wishlist that
 * can only reference existing lessons only ever tells us about work we already
 * did.
 *
 * `?src=` on the link tags a batch ("demo-mumbai-aug"), so responses stay
 * attributable to a session even when the individuals stay anonymous.
 * `?topic=` preselects the demand form.
 */

import { createFileRoute, useSearch, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  LearnRoot, LearnTopBar, useLearnTheme, glassPanel, inkMuted, LEARN_ALL_SUBJECTS,
} from '../components/learn/learnShared';
import { FeedbackSurvey } from '../components/learn/FeedbackSurvey';
import { TOPIC_TEACHER, FRAMEWORK_FORM, VIDEO_STUDENT } from '../components/learn/feedbackForms';
import { useLearnAuth } from '../components/learn/LearnAuthProvider';

type Search = { src?: string; topic?: string; role?: string };

export const Route = createFileRoute('/learn/feedback')({
  component: FeedbackPage,
  validateSearch: (s: Record<string, unknown>): Search => ({
    src: typeof s.src === 'string' ? s.src : undefined,
    topic: typeof s.topic === 'string' ? s.topic : undefined,
    role: typeof s.role === 'string' ? s.role : undefined,
  }),
});

type Intent = 'topic' | 'framework' | 'student';

function FeedbackPage() {
  const { theme, toggle } = useLearnTheme();
  const { profile } = useLearnAuth();
  const search = useSearch({ from: '/learn/feedback' }) as Search;
  const [intent, setIntent] = useState<Intent | null>(search.topic ? 'topic' : null);

  const form =
    intent === 'topic' ? TOPIC_TEACHER : intent === 'framework' ? FRAMEWORK_FORM : VIDEO_STUDENT;
  const role = intent === 'student' ? 'STUDENT' : 'TEACHER';

  return (
    <LearnRoot theme={theme} subjects={LEARN_ALL_SUBJECTS}>
      <LearnTopBar
        theme={theme}
        onToggle={toggle}
        right={
          <Link
            to="/learn"
            className={cn('mr-1 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium hover:text-[#191831] dark:hover:text-white', inkMuted)}
          >
            <ArrowLeft className="h-4 w-4" /> Lessons
          </Link>
        }
      />

      <main className="mx-auto max-w-[760px] px-6 pb-24 pt-10">
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Tell us</p>
        <h1 className="mt-1 font-serif text-[40px] leading-[1.14] tracking-tight">
          What should we build, and what are we getting wrong?
        </h1>
        <p className={cn('mt-3 max-w-[52ch] text-[15.5px] leading-relaxed', inkMuted)}>
          You teach this every year and we do not. Two minutes here changes what we make next.
        </p>

        {!intent ? (
          <div className="mt-8 flex flex-col gap-3">
            <IntentCard
              onClick={() => setIntent('topic')}
              title="I want to tell you about a topic"
              body="What your students struggle with, what you cannot explain well today, and what we should build — including chapters we have not made yet."
            />
            <IntentCard
              onClick={() => setIntent('framework')}
              title="I want to give feedback on the whole thing"
              body="Not one lesson — the format, the checks, whether this belongs in your classroom at all."
            />
            <IntentCard
              onClick={() => setIntent('student')}
              title="I am a student"
              body="Tell us how the lessons feel to learn from."
            />
          </div>
        ) : (
          <>
            <button
              onClick={() => setIntent(null)}
              className={cn('mt-8 inline-flex items-center gap-1.5 text-[13px] font-semibold hover:text-[#5B2EE5]', inkMuted)}
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Choose something else
            </button>
            <FeedbackSurvey
              className="mt-3"
              form={form}
              role={role}
              chapter={search.topic ?? null}
              source={search.src ?? null}
              learnerAccountId={profile?.id ?? null}
              prefillName={profile?.displayName ?? null}
              prefillEmail={profile?.email ?? null}
              askIdentity
            />
          </>
        )}
      </main>
    </LearnRoot>
  );
}

function IntentCard({ title, body, onClick }: { title: string; body: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        glassPanel,
        'group p-5 text-left transition-all hover:-translate-y-0.5 hover:bg-white/[0.92] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5] dark:hover:bg-[#181A2A]/[0.85]',
      )}
    >
      <p className="font-serif text-[21px] leading-[1.28] tracking-tight">{title}</p>
      <p className={cn('mt-1.5 text-[14px] leading-relaxed', inkMuted)}>{body}</p>
    </button>
  );
}
